import { Alert, AlertTitle, Box, Button, Collapse, Divider, Typography } from "@mui/material";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { Flex, HStack, colors } from "@zfp-akademie-a-s/zfp-design";
import { CalendarIcon, ChevronDownIcon, LocationIcon, PhoneIcon, ShieldIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { IdCardIcon, MailIcon } from "Components/_Shared/Icons/Icons";
import type { MangoRepresentativeDetail } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientPhone, formatPersonalIdentificationNumber } from "Infrastructure/Utils/ClientUtils";
import { useState } from "react";
import { formatBirthDateWithAge, formatCityAndPostalCode } from "./ClientCardDetail.helpers";
import { InfoItem } from "./InfoItem";

const ICON_CIRCLE_SIZE = 36;

type Props = {
  representative: MangoRepresentativeDetail;
  showAdultWarning: boolean;
};

export const ClientCardLegalRepresentative = ({ representative, showAdultWarning }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formatLocale = useFormatLocale();
  const { t } = useResource();

  const fullName = getClientFullName({
    titlePrefix: representative.titlePrefix,
    name: representative.firstName,
    surname: representative.surname,
    titlePostfix: representative.titlePostfix,
  }).trim();
  const address = formatCityAndPostalCode(representative.address);

  return (
    <Box>
      <Divider />
      <HStack px={Spacing.XL} py={Spacing.L} spacing={Spacing.L} alignItems="center">
        <Flex
          flexShrink={0}
          width={ICON_CIRCLE_SIZE}
          height={ICON_CIRCLE_SIZE}
          borderRadius="50%"
          alignItems="center"
          justifyContent="center"
          color="text.secondary"
          bgcolor={colors.backgroundTertiary}
        >
          <ShieldIcon sx={{ fontSize: 20 }} />
        </Flex>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t(Resources.Clients.ClientCard.LegalRepresentative.Title)}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {fullName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => setIsExpanded((prev) => !prev)}
          endIcon={
            <ChevronDownIcon
              fontSize="small"
              sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
            />
          }
        >
          {t(
            isExpanded
              ? Resources.Clients.ClientCard.LegalRepresentative.HideDetails
              : Resources.Clients.ClientCard.LegalRepresentative.ShowDetails,
          )}
        </Button>
      </HStack>

      <Collapse in={isExpanded}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(2, minmax(0, 1fr))"
          rowGap={Spacing.M}
          columnGap={Spacing.XXL}
          pr={Spacing.XL}
          pb={Spacing.XL}
          // lines the details up under the name rather than under the icon
          sx={(theme) => ({ pl: `calc(${theme.spacing(Spacing.XL + Spacing.L)} + ${ICON_CIRCLE_SIZE}px)` })}
        >
          <InfoItem icon={<CalendarIcon />} text={formatBirthDateWithAge(representative.birthDate, formatLocale, t)} />
          <InfoItem icon={<PhoneIcon />} text={formatClientPhone(representative.phonePrefix, representative.phoneNationalNumber)} />
          <InfoItem icon={<IdCardIcon />} text={formatPersonalIdentificationNumber(representative.personalIdentificationNumber)} />
          <InfoItem icon={<MailIcon />} text={representative.email} />
          {address && (
            <Box gridColumn="1 / -1">
              <InfoItem icon={<LocationIcon />} text={address} />
            </Box>
          )}
        </Box>
      </Collapse>

      {showAdultWarning && (
        <Box px={Spacing.XL} pb={Spacing.XL}>
          <Alert severity="warning">
            <AlertTitle>{t(Resources.Clients.ClientCard.Alerts.AdultWithLegalRepresentative.Title)}</AlertTitle>
            {t(Resources.Clients.ClientCard.Alerts.AdultWithLegalRepresentative.Text)}
          </Alert>
        </Box>
      )}
    </Box>
  );
};
